import React, { useRef, useState, useEffect } from "react";
import { Box, Typography, Skeleton } from "@mui/material";
import useIntersectionObserver from "../../../common/hooks/useIntersectionObserver";
import Cemetery from "../../../assets/media/image1.jpeg";

interface ServicePageCardProps {
  serviceCategory?: any;
  onClick: (serviceId: number) => void;
}

const ServicePageCard: React.FC<ServicePageCardProps> = ({
  serviceCategory,
  onClick,
}) => {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const isVisible = useIntersectionObserver(cardRef, { threshold: 0.1 });
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState<boolean>(true);

  useEffect(() => {
    if (isVisible) {
      const imageUrl = serviceCategory?.fileInfo?.[0]?.url;

      if (imageUrl) {
        setImageLoading(true);
        const img = new Image();
        img.src = imageUrl;
        img.onload = () => {
          setImageSrc(imageUrl);
          setImageLoading(false);
        };
        img.onerror = () => {
          setImageSrc(Cemetery);
          setImageLoading(false);
        };
      } else {
        setImageSrc(Cemetery);
        setImageLoading(false);
      }
    }
  }, [isVisible, serviceCategory]);

  return (
    <Box
      ref={cardRef}
      className="carousel-card"
      sx={{
        height: { xs: 350, sm: 420, md: 489 },
        backgroundImage: imageSrc ? `url(${imageSrc})` : undefined,
        backgroundColor: "#f0f0f0",
        backgroundSize: "cover",
        backgroundPosition: "center",
        borderRadius: 2,
        position: "relative",
        mx: { xs: 0, sm: 1, md: 2 },
        overflow: "hidden",
        transition: "background-image 0.3s ease-in-out",
        "&:hover .hover-overlay": {
          opacity: 1,
          transform: "translateY(0)",
        },
        cursor: "pointer",
      }}
      onClick={() => serviceCategory?.id && onClick(serviceCategory.id)}
    >
      {imageLoading && (
        <Skeleton
          variant="rectangular"
          animation="wave"
          width="100%"
          height="100%"
          sx={{
            position: "absolute",
            inset: 0,
            borderRadius: 2,
            zIndex: 1,
          }}
        />
      )}

      {serviceCategory?.services?.length > 0 && (
        <Box
          className="carousel-card__overlay hover-overlay"
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            transform: "translateY(100%)",
            cursor: "pointer",
            zIndex: 999,
            transition: "transform 0.35s ease, opacity 0.35s ease",
            opacity: 0,
            background:
              "linear-gradient(180deg, rgba(16,65,69,0.97) 0%, rgba(16,65,69,0.97) 100%)",
          }}
        >
          <Box className="carousel-card__overlay-content">
            <Typography
              fontWeight="bold"
              className="carousel-card__title"
              sx={{
                fontSize: { xs: 20, sm: 24, md: 28 },
                mb: 1,
                color: "#fff",
                fontWeight: 600,
              }}
            >
              {serviceCategory?.name}
            </Typography>

            <Typography
              className="carousel-card__subheading"
              sx={{
                fontSize: { xs: 14, sm: 16, md: 20 },
                mt: { xs: 1, sm: 2 },
                fontWeight: 600,
                color: "#fff",
              }}
            >
              {serviceCategory?.description}
            </Typography>
            <Typography
              className="carousel-card__subheading"
              sx={{
                fontSize: { xs: 14, sm: 16, md: 18 },
                mt: { xs: 1, sm: 2 },
                fontWeight: 600,
                color: "#fff",
              }}
            >
              Common Services
            </Typography>

            <Box
              className="carousel-card__subservices"
              sx={{
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
                mt: { xs: 1, sm: 2 },
              }}
            >
              {serviceCategory?.services
                ?.slice(0, 3)
                .map((subService: any, serviceIdx: number) => (
                  <Box
                    key={subService?.id || serviceIdx}
                    className="subService-box service-name-box"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: { xs: 0.8, sm: 1.2 },
                      bgcolor: "rgba(255, 255, 255, 0.15)",
                      color: "#fff",
                      borderRadius: "8px",
                      fontSize: { xs: "12px", sm: "14px", md: "16px" },
                      fontWeight: 600,
                      textAlign: "center",
                      maxWidth: "100%",
                      minHeight: "25px",
                      width: "100%",
                      boxSizing: "border-box",
                    }}
                  >
                    <p className="service-name">{subService?.name}</p>
                  </Box>
                ))}

              {serviceCategory?.services?.length > 3 && (
                <Box
                  className="subService-box subService-box--more service-name-box"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: { xs: 0.8, sm: 1.2 },
                    bgcolor: "rgba(255, 255, 255, 0.15)",
                    color: "#fff",
                    borderRadius: "8px",
                    fontSize: { xs: "12px", sm: "14px", md: "16px" },
                    fontWeight: 600,
                    textAlign: "center",
                    maxWidth: "100%",
                    width: "100%",
                    minHeight: "25px",
                    boxSizing: "border-box",
                  }}
                >
                  +{serviceCategory?.services?.length - 3} More
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      )}

      <Box
        className="carousel-card__bottom"
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: "#B8B7C8E5",
          p: { xs: 1.5, sm: 2 },
          borderBottomLeftRadius: 8,
          borderBottomRightRadius: 8,
          minHeight: { xs: 60, sm: 75 },
          zIndex: 3,
        }}
      >
        <Typography
          className="carousel-card__name"
          sx={{
            fontSize: { xs: 18, sm: 22, md: 25 },
            fontWeight: 600,
          }}
        >
          {serviceCategory?.name || ""}
        </Typography>
      </Box>
    </Box>
  );
};

export default ServicePageCard;
